import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiGetAuth, apiPatchAuth, apiDeleteAuth, isApiConfigured, ApiError } from "@/services/http";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Review {
  id:           number;
  businessName: string;
  businessSlug: string;
  authorName:   string;
  authorEmail:  string | null;
  rating:       number;
  comment:      string;
  status:       "pending" | "approved" | "removed";
  submittedAt:  string;
  flagReason?:  string;
}

// ── Demo data ─────────────────────────────────────────────────────────────────

const DEMO_REVIEWS: Review[] = [
  {
    id: 301, businessName: "Mangalore Harbour Fish House", businessSlug: "mangalore-harbour-fish-house",
    authorName: "Deepak Nayak", authorEmail: "deepak@mail.com", rating: 5,
    comment: "Absolutely the best seafood in Mangalore. The Prawn Ghee Roast is divine and the service was top notch! Highly recommend visiting during the weekend for the freshest catch.",
    status: "pending", submittedAt: "2026-09-12T09:15:00Z",
  },
  {
    id: 302, businessName: "Udupi Sri Krishna Bhavan", businessSlug: "udupi-sri-krishna-bhavan",
    authorName: "Meera Shetty", authorEmail: "meera@webmail.in", rating: 4,
    comment: "Authentic Udupi cuisine at its finest. The Masala Dosa crispy and the Sambhar is spot on. Slightly busy during lunch hours but worth the wait.",
    status: "pending", submittedAt: "2026-09-11T14:30:00Z",
  },
  {
    id: 303, businessName: "Karavali Auto Works", businessSlug: "karavali-auto-works",
    authorName: "Fake User 999", authorEmail: "spam@throwaway.io", rating: 1,
    comment: "WORST EVER. Don't go here. Visit [other-shop-link] instead for better prices. Total scam operation run by criminals.",
    status: "pending", submittedAt: "2026-09-10T17:45:00Z",
    flagReason: "Suspected spam/competitor review with external link",
  },
  {
    id: 304, businessName: "Bhatkal Silk Emporium", businessSlug: "bhatkal-silk-emporium",
    authorName: "Reshma Mogaveera", authorEmail: "reshma@icloud.com", rating: 5,
    comment: "Beautiful collection of Bhatkal silk sarees. The staff were patient and knowledgeable. Got a lovely set for my daughter's wedding.",
    status: "approved", submittedAt: "2026-09-09T10:20:00Z",
  },
  {
    id: 305, businessName: "Puttur Pooja Flowers", businessSlug: "puttur-pooja-flowers",
    authorName: "Suresh Kumar", authorEmail: null, rating: 3,
    comment: "Decent selection but could be more fresh. Prices are reasonable for the area.",
    status: "approved", submittedAt: "2026-09-08T08:10:00Z",
  },
  {
    id: 306, businessName: "Mangalore Harbour Fish House", businessSlug: "mangalore-harbour-fish-house",
    authorName: "Bot Account", authorEmail: "bot001@spam.xyz", rating: 5,
    comment: "Great great great great great great. Best fish in world. Visit now click link promo discount coupon code.",
    status: "removed", submittedAt: "2026-09-07T22:05:00Z",
    flagReason: "Removed — bot pattern detected, promotional spam",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <i key={s} className={`text-sm ${s <= rating ? "ri-star-fill text-amber-400" : "ri-star-line text-background-300"}`} />
      ))}
    </div>
  );
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const d = Math.floor(diff / 86400000);
  if (d < 1) return "Today";
  if (d === 1) return "Yesterday";
  return `${d}d ago`;
}

const STATUS_COLORS: Record<string, string> = {
  pending:  "bg-amber-100 text-amber-700",
  approved: "bg-emerald-100 text-emerald-700",
  removed:  "bg-rose-100 text-rose-700",
};

// ── Component ─────────────────────────────────────────────────────────────────

export default function ReviewsModeration() {
  const { accessToken } = useAuth();
  const [reviews,  setReviews]  = useState<Review[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [filter,   setFilter]   = useState<"all" | "pending" | "approved" | "removed">("pending");
  const [error,    setError]    = useState("");

  useEffect(() => {
    if (!isApiConfigured() || !accessToken) {
      setTimeout(() => { setReviews(DEMO_REVIEWS); setLoading(false); }, 350);
      return;
    }
    apiGetAuth<{ data: Review[] }>("/admin/reviews", accessToken)
      .then((res) => setReviews(res.data ?? []))
      .catch(() => setReviews(DEMO_REVIEWS))
      .finally(() => setLoading(false));
  }, [accessToken]);

  async function handleApprove(id: number) {
    setActionId(id); setError("");
    try {
      if (!isApiConfigured() || !accessToken) {
        await new Promise((r) => setTimeout(r, 500));
      } else {
        await apiPatchAuth(`/admin/reviews/${id}/approve`, {}, accessToken);
      }
      setReviews((prev) => prev.map((r) => r.id === id ? { ...r, status: "approved" } : r));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed.");
    } finally { setActionId(null); }
  }

  async function handleRemove(id: number) {
    setActionId(id); setError("");
    try {
      if (!isApiConfigured() || !accessToken) {
        await new Promise((r) => setTimeout(r, 500));
      } else {
        await apiDeleteAuth(`/admin/reviews/${id}`, accessToken);
      }
      setReviews((prev) => prev.map((r) => r.id === id ? { ...r, status: "removed" } : r));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Action failed.");
    } finally { setActionId(null); }
  }

  const pending   = reviews.filter((r) => r.status === "pending").length;
  const displayed = reviews.filter((r) => filter === "all" || r.status === filter);

  const filterTabs: { key: typeof filter; label: string }[] = [
    { key: "pending",  label: `Pending (${pending})` },
    { key: "approved", label: "Approved" },
    { key: "removed",  label: "Removed" },
    { key: "all",      label: "All" },
  ];

  return (
    <div>
      {/* Stats */}
      <div className="mb-6 grid grid-cols-3 gap-4">
        {[
          { label: "Awaiting Moderation", value: pending, icon: "ri-time-line", color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Approved Reviews",    value: reviews.filter((r) => r.status === "approved").length, icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Removed (Spam)",      value: reviews.filter((r) => r.status === "removed").length,  icon: "ri-spam-2-line", color: "text-rose-600", bg: "bg-rose-50" },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-4 rounded-xl border border-background-200 bg-background-50 p-5">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${s.bg}`}>
              <i className={`${s.icon} text-xl ${s.color}`} />
            </div>
            <div>
              <p className="font-heading text-2xl font-bold text-foreground-950">{loading ? "—" : s.value}</p>
              <p className="text-xs text-foreground-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-6 py-4">
          <h2 className="font-heading text-base font-bold text-foreground-950">Review Moderation</h2>
          {pending > 0 && (
            <span className="rounded-full bg-amber-100 px-3 py-0.5 text-xs font-bold text-amber-700">
              {pending} pending
            </span>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 overflow-x-auto border-b border-background-200 px-5 pt-3">
          {filterTabs.map((t) => (
            <button
              key={t.key} type="button" onClick={() => setFilter(t.key)}
              className={`cursor-pointer whitespace-nowrap border-b-2 px-3 pb-2.5 text-xs font-semibold transition-colors ${
                filter === t.key
                  ? "border-primary-500 text-primary-700"
                  : "border-transparent text-foreground-500 hover:text-foreground-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mx-6 mt-4 rounded-lg bg-rose-50 px-4 py-3 text-sm text-rose-700">
            <i className="ri-error-warning-line mr-2" />{error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
              <i className="ri-star-line text-2xl text-foreground-400" />
            </div>
            <p className="text-sm font-medium text-foreground-700">No reviews in this filter</p>
          </div>
        ) : (
          <div className="divide-y divide-background-100">
            {displayed.map((rev) => (
              <div key={rev.id} className={`px-6 py-5 ${rev.flagReason ? "bg-rose-50/30" : ""}`}>
                <div className="flex flex-wrap items-start gap-3">
                  {/* Avatar */}
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-700">
                    {rev.authorName[0].toUpperCase()}
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="text-sm font-semibold text-foreground-900">{rev.authorName}</p>
                      <StarRating rating={rev.rating} />
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold capitalize ${STATUS_COLORS[rev.status]}`}>
                        {rev.status}
                      </span>
                    </div>
                    <p className="mt-0.5 text-xs text-foreground-500">
                      On <a href={`/business/${rev.businessSlug}`} className="font-medium text-primary-600 hover:underline" target="_blank" rel="noopener noreferrer">{rev.businessName}</a>
                      {" · "}{timeAgo(rev.submittedAt)}
                    </p>

                    <p className="mt-2 text-sm leading-relaxed text-foreground-700">{rev.comment}</p>

                    {rev.flagReason && (
                      <div className="mt-2 flex items-start gap-1.5 rounded-md bg-rose-100 px-3 py-2 text-xs text-rose-700">
                        <i className="ri-flag-line mt-0.5 shrink-0" />
                        {rev.flagReason}
                      </div>
                    )}

                    {/* Actions */}
                    {rev.status === "pending" && (
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          disabled={actionId === rev.id}
                          onClick={() => handleApprove(rev.id)}
                          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white whitespace-nowrap transition-colors hover:bg-emerald-700 disabled:opacity-60"
                        >
                          {actionId === rev.id ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-checkbox-circle-line text-sm" />}
                          Approve
                        </button>
                        <button
                          type="button"
                          disabled={actionId === rev.id}
                          onClick={() => handleRemove(rev.id)}
                          className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-3 py-1.5 text-xs font-semibold text-rose-700 whitespace-nowrap transition-colors hover:bg-rose-100 disabled:opacity-60"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                          Remove
                        </button>
                      </div>
                    )}
                    {rev.status !== "pending" && (
                      <p className={`mt-2 text-xs font-semibold ${rev.status === "approved" ? "text-emerald-700" : "text-rose-700"}`}>
                        <i className={`mr-1 ${rev.status === "approved" ? "ri-checkbox-circle-line" : "ri-close-circle-line"}`} />
                        {rev.status === "approved" ? "Approved and visible on listing." : "Removed — hidden from listing."}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}